import styled, { keyframes } from "styled-components";

// Painel de ondas ao vivo. Dois modos de exibição:
//   TV   → cartões rotacionados (TV em pé/retrato), cabem ~20 ondas
//   DESK → cartões horizontais legíveis no monitor, com os dados da onda
// Fundo claro + barras pretas (essencial pro leitor de código de barras).
const c = {
  bg: "#eef1f5",
  card: "#ffffff",
  border: "#e3e7ec",
  slate: "#1e293b",
  txt2: "#5b6675",
  txt3: "#9aa4b2",
  ok: "#12965a",
  okBg: "#e9f7ef",
  warn: "#b45309",
  warnBg: "#fef3c7",
  danger: "#dc2626",
  dangerBg: "#fdecec",
  accent: "#2563eb",
  accentBg: "#eef4ff",
  sans: "'Inter', 'Segoe UI', Roboto, Arial, sans-serif",
  mono: "'Space Mono', 'Roboto Mono', Consolas, monospace",
};

const fadeIn = keyframes`from { opacity: 0; } to { opacity: 1; }`;

export const PageWrap = styled.div`
  width: 100%;
  height: 100vh;
  box-sizing: border-box;
  padding: 1.4vh 1vw;
  background: ${c.bg};
  font-family: ${c.sans};
  display: flex;
  flex-direction: column;
  overflow: ${({ $scroll }) => ($scroll ? "auto" : "hidden")};
`;

// ─── MODO TV ────────────────────────────────────────────────────────────────
// Grade de cartões: 2 linhas × N colunas (até 10 colunas = 20 ondas).
// A posição exata de cada onda é definida por gridColumn/gridRow no componente.
export const List = styled.div`
  flex: 1;
  min-height: 0;
  display: grid;
  grid-template-rows: 1fr 1fr;
  grid-auto-columns: 1fr;
  grid-auto-flow: column;
  gap: 6vh 2vw; /* linha / coluna */
`;

export const Row = styled.div`
  min-width: 0;
  min-height: 0;
  height: 100%;
  box-sizing: border-box;
  display: grid;
  grid-template-columns: auto 1fr; /* esquerda (nº + rank) | código de barras */
  grid-template-rows: 1fr;         /* 1 linha com altura definida — evita o barcode estourar */
  padding: 1.4vh 0.5vw;
  border-radius: 14px;
  animation: ${fadeIn} 0.2s ease;
  background: ${c.card};
  border: 2px solid ${c.border};
  box-shadow: 0 2px 8px rgba(16, 24, 40, 0.06);
  cursor: pointer;
  transition: border-color 0.12s, box-shadow 0.12s;
  &:hover {
    border-color: ${c.accent};
    box-shadow: 0 4px 14px rgba(37, 99, 235, 0.18);
  }

  /* Coluna esquerda: pilha ao longo do cartão, na ordem em que se lê com a TV
     em pé — separadores, número + embarque, % concluída e o rank no canto.
     Largura TRAVADA em 3.6vw (era min-width): tudo cabe na folga que já existia
     ao lado do número, então a coluna do código de barras não muda de tamanho.
     O overflow escondido garante que onda nenhuma vaze pro cartão vizinho. */
  .left {
    position: relative;
    width: 3.6vw;
    min-width: 0;
    min-height: 0;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 1.1vh;
    padding-bottom: 3.9vw; /* reserva o canto onde o rank fica sobreposto */
    overflow: hidden;
  }

  /* Cada bloco escreve no sentido do cartão; os filhos viram linhas paralelas
     (que, com a TV em pé, aparecem uma abaixo da outra). */
  .seps,
  .mid,
  .pct {
    writing-mode: vertical-rl;
    transform: rotate(180deg);
    min-height: 0;
    max-width: 100%;
  }

  /* Separadores: um nome por linha, encostados na borda esquerda do cartão,
     com reticências nos nomes muito longos */
  .seps {
    align-self: flex-start;
    max-height: 32%;
    overflow: hidden;
  }
  .seps > div {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    font-size: 0.62vw;
    line-height: 1.35;
    font-weight: 600;
    letter-spacing: 0.01em;
    color: ${c.txt3};
  }

  /* Número da onda com a data de embarque ao lado.
     text-align nos dois centraliza a data em relação ao número (no modo
     vertical o alinhamento de texto corre no sentido do cartão). */
  .mid {
    display: block;
    max-height: 46%;
    overflow: hidden;
  }
  .mid .onda {
    white-space: nowrap;
    text-align: center;
    font-family: ${c.mono} !important; /* vence o * { font-family: nunito !important } global */
    font-size: 2vw;
    font-weight: 500;
    line-height: 1.05;
    color: ${c.slate};
  }
  .mid .dl {
    white-space: nowrap;
    text-align: center;
    font-size: 0.66vw;
    line-height: 1.25;
    font-weight: 700;
    color: ${c.txt2};
  }

  /* Percentual concluído — mesmas faixas de cor da coerência */
  .pct {
    white-space: nowrap;
    font-family: ${c.mono} !important; /* vence o global */
    font-size: 0.82vw;
    font-weight: 800;
    line-height: 1;
    color: ${c.txt2};
    &.ok { color: ${c.ok}; }
    &.warn { color: ${c.warn}; }
    &.danger { color: ${c.danger}; }
  }

  .rank {
    position: absolute;
    left: 0;
    bottom: 0.6vh;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 3vw;
    height: 3vw;
    border-radius: 8px;
    font-family: ${c.mono} !important; /* vence o global */
    font-size: 1.5vw;
    font-weight: 800;
    color: #fff;
    background: ${c.slate};
    transform: rotate(270deg);
  }

  /* Coluna direita: código de barras — ocupa a altura definida (1fr) sem estourar */
  .barcode {
    position: relative;
    min-width: 0;
    min-height: 0;
    height: 100%;
    overflow: hidden;
  }
  /* O wrapper do Code39 (fill) fica FORA do fluxo, preso ao tamanho da .barcode.
     Sem isso o SVG (min-height:auto de item de grid) empurra o bloco além da linha. */
  .barcode > span {
    position: absolute;
    inset: 0;
    display: flex;
    align-items: center;
    justify-content: center;
  }
`;

// ─── MODO COMPUTADOR ────────────────────────────────────────────────────────
export const DeskList = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 14px;
  align-content: start;
  padding-bottom: 16px;
`;

export const DeskCard = styled.div`
  background: ${c.card};
  border: 1px solid ${c.border};
  border-left: 4px solid ${({ $overdue }) => ($overdue ? c.danger : c.accent)};
  border-radius: 12px;
  padding: 12px 14px;
  cursor: pointer;
  animation: ${fadeIn} 0.15s ease;
  box-shadow: 0 1px 3px rgba(16, 24, 40, 0.06);
  transition: box-shadow 0.12s, transform 0.12s;
  &:hover {
    box-shadow: 0 6px 18px rgba(16, 24, 40, 0.12);
    transform: translateY(-1px);
  }

  .head {
    display: flex;
    align-items: center;
    gap: 10px;
    margin-bottom: 6px;
  }
  .rank {
    flex: 0 0 auto;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 30px;
    height: 30px;
    padding: 0 8px;
    border-radius: 8px;
    background: ${c.slate};
    color: #fff;
    font-family: ${c.mono};
    font-size: 15px;
    font-weight: 800;
  }
  .num {
    font-family: ${c.mono};
    font-size: 26px;
    font-weight: 800;
    color: ${c.slate};
    line-height: 1;
  }
  .barcode {
    height: 62px;
    margin: 6px 0 10px;
    display: flex;
    align-items: center;
    justify-content: center;
  }
  .meta {
    display: flex;
    flex-wrap: wrap;
    gap: 4px 14px;
    font-size: 12px;
    color: ${c.txt2};
  }
  .meta b {
    color: ${c.slate};
    font-family: ${c.mono};
  }
  .chips {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
    margin-top: 10px;
  }
`;

// ─── CHIPS ──────────────────────────────────────────────────────────────────
export const Chip = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 3px 9px;
  border-radius: 999px;
  font-size: 11px;
  font-weight: 800;
  white-space: nowrap;
  ${({ $tone }) =>
    $tone === "ok"
      ? `background:${c.okBg};color:${c.ok};`
      : $tone === "warn"
        ? `background:${c.warnBg};color:${c.warn};`
        : $tone === "danger"
          ? `background:${c.dangerBg};color:${c.danger};`
          : $tone === "accent"
            ? `background:${c.accentBg};color:${c.accent};`
            : "background:#eef2f7;color:#5b6675;"}
`;

// ─── ALTERNADOR DE VISÃO ────────────────────────────────────────────────────
export const ViewToggle = styled.button`
  position: fixed;
  right: 14px;
  bottom: 14px;
  z-index: 900;
  padding: 8px 14px;
  border-radius: 999px;
  border: 1px solid ${c.border};
  background: ${c.card};
  color: ${c.txt2};
  font-family: ${c.sans};
  font-size: 12px;
  font-weight: 700;
  cursor: pointer;
  box-shadow: 0 2px 10px rgba(16, 24, 40, 0.14);
  opacity: ${({ $dim }) => ($dim ? 0.12 : 1)};
  transition: opacity 0.3s, border-color 0.12s, color 0.12s;
  &:hover { opacity: 1; border-color: ${c.accent}; color: ${c.accent}; }
`;

// ─── MODAL ──────────────────────────────────────────────────────────────────
export const ModalOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(15, 23, 42, 0.55);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  z-index: 1000;
  animation: ${fadeIn} 0.12s ease;
`;

export const ModalCard = styled.div`
  background: ${c.card};
  border-radius: 16px;
  width: 100%;
  max-width: 900px;
  max-height: 86vh;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  box-shadow: 0 20px 60px rgba(15, 23, 42, 0.35);
  font-family: ${c.sans};
`;

export const ModalHead = styled.div`
  flex: 0 0 auto;
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  padding: 16px 20px;
  border-bottom: 1px solid ${c.border};

  .title {
    display: flex;
    align-items: center;
    gap: 10px;
    flex-wrap: wrap;
  }
  .rank {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 30px;
    height: 30px;
    padding: 0 8px;
    border-radius: 8px;
    background: ${c.slate};
    color: #fff;
    font-family: ${c.mono};
    font-size: 14px;
    font-weight: 800;
  }
  h3 {
    margin: 0;
    font-family: ${c.mono};
    font-size: 22px;
    font-weight: 800;
    color: ${c.slate};
  }
`;

export const ModalClose = styled.button`
  border: none;
  background: #f1f5f9;
  color: ${c.txt2};
  width: 32px;
  height: 32px;
  border-radius: 8px;
  font-size: 20px;
  line-height: 1;
  cursor: pointer;
  flex: 0 0 auto;
  transition: background 0.12s;
  &:hover { background: #e2e8f0; }
`;

export const ModalBody = styled.div`
  flex: 1;
  min-height: 0;
  overflow: auto;
  overscroll-behavior: contain;
`;

export const SummaryGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(120px, 1fr));
  gap: 10px;
  padding: 14px 20px;
  border-bottom: 1px solid ${c.border};
  background: #fafbfc;

  .cell .k {
    display: block;
    font-size: 10px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    color: ${c.txt3};
    margin-bottom: 2px;
  }
  .cell .v {
    font-size: 15px;
    font-weight: 800;
    color: ${c.slate};
    font-family: ${c.mono};
  }
  .cell .v.sm {
    font-size: 12px;
    font-weight: 600;
    font-family: ${c.sans};
    color: ${c.txt2};
  }
`;

export const OrderTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;

  thead th {
    position: sticky;
    top: 0;
    background: #fafbfc;
    color: ${c.txt3};
    font-size: 10px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    text-align: left;
    padding: 10px 20px;
    border-bottom: 1px solid ${c.border};
    white-space: nowrap;
  }
  tbody td {
    padding: 10px 20px;
    border-bottom: 1px solid ${c.border};
    color: ${c.slate};
    vertical-align: middle;
  }
  tbody tr:last-child td { border-bottom: none; }
  tbody tr:hover td { background: #f8fafc; }
  .mono { font-family: ${c.mono}; }
  .dim { color: ${c.txt2}; }
  .c { text-align: center; }
`;

export const Empty = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${c.txt3};
  font-size: 2.4vh;
  padding: 40px 20px;
  text-align: center;
`;

export const Loading = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${c.txt3};
  font-size: 2vh;
  padding: 40px 20px;
`;
